import {
  Anchor,
  Badge,
  Collapse,
  ConditionalRendering,
  Text,
  UnstyledButton
} from "@marketplace/ui";
import { Link, useLocation } from "@tanstack/react-router";
import clsx from "clsx";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdKeyboardArrowRight } from "react-icons/md";

// Prefer Anchor + Link here to allow dynamic href strings without RouterLink's strict `to` typing
import type {
  MenuItem,
  SidebarMenuProps,
  SidebarMenuSectionProps
} from "./sidebar.types";

const SidebarMenuItemBadge = ({ count }: { count?: number | null }) => {
  if (!count || count < 1) return null;

  return (
    <Badge className="pointer-events-none shrink-0 min-w-6 h-6 px-2 flex items-center justify-center rounded-full bg-[#FF6A2B] text-white border-0 normal-case">
      {count > 99 ? "99+" : count}
    </Badge>
  );
};

const SidebarMenuItemLabel = ({
  label,
  badgeCount
}: {
  label: string;
  badgeCount?: number | null;
}) => {
  return (
    <div className="grow flex items-center gap-x-2">
      <Text className="whitespace-nowrap">{label}</Text>
      <SidebarMenuItemBadge count={badgeCount} />
    </div>
  );
};

const SidebarMenuItem = ({
  label,
  link,
  icon,
  badgeCount,
  onClick,
  children,
  initiallyOpen
}: Omit<MenuItem, "key">) => {
  const hasChildren = Array.isArray(children);
  const [opened, setOpened] = useState(initiallyOpen || false);
  const { pathname } = useLocation();

  const isActive = useMemo(() => {
    if (pathname !== "/" && link === "/") return false;

    return link ? pathname.startsWith(link) : false;
  }, [pathname, link]);

  const isAnyChildActive = useMemo(() => {
    const normalizedPathname = pathname.replace(/\/$/, "");
    return children?.some((item) =>
      normalizedPathname.startsWith(item.link.replace(/\/$/, ""))
    );
  }, [children, pathname]);

  const isOpen = opened || !!isAnyChildActive;

  const staticBaseClasses =
    "w-full py-[10px] px-2 flex gap-x-[10px] items-center text-fjord-grey-300 rounded-md hover:bg-[#F1F9FF] hover:text-noble-blue-500";

  const baseClasses = clsx(
    staticBaseClasses,
    isActive && !isAnyChildActive && "bg-[#F1F9FF] !text-noble-blue-500"
  );

  const childrenLinks = useMemo(() => {
    if (!hasChildren) return null;
    return children.map((item) => (
      <Anchor
        component={Link}
        key={item.key ?? item.link}
        to={item.link}
        underline="never"
        className={clsx(
          staticBaseClasses,
          pathname.startsWith(item.link) && "bg-[#F1F9FF] !text-noble-blue-500"
        )}
      >
        <div className="w-[24px] h-[24px]" />
        <SidebarMenuItemLabel label={item.label} badgeCount={item.badgeCount} />
      </Anchor>
    ));
  }, [children, staticBaseClasses, hasChildren, pathname]);

  if (!hasChildren) {
    if (link) {
      return (
        <Anchor
          component={Link}
          to={link}
          underline="never"
          className={baseClasses}
          {...(onClick ? { onClick } : {})}
        >
          <div className="w-[24px] h-[24px] flex justify-center items-center">
            {icon}
          </div>
          <SidebarMenuItemLabel label={label} badgeCount={badgeCount} />
        </Anchor>
      );
    }

    // Fallback when link is not provided on a leaf item
    return (
      <UnstyledButton className={baseClasses} {...(onClick ? { onClick } : {})}>
        <div className="w-[24px] h-[24px] flex justify-center items-center">
          {icon}
        </div>
        <SidebarMenuItemLabel label={label} badgeCount={badgeCount} />
      </UnstyledButton>
    );
  }

  return (
    <>
      <UnstyledButton
        className={baseClasses}
        onClick={() => setOpened((o: boolean) => !o)}
      >
        <div className="w-[24px] h-[24px] flex justify-center items-center">
          {icon}
        </div>
        <SidebarMenuItemLabel label={label} badgeCount={badgeCount} />
        <MdKeyboardArrowRight
          className="w-5 h-5 transition-transform shrink-0 self-end"
          style={{
            transform: isOpen ? "rotate(90deg)" : "none"
          }}
        />
      </UnstyledButton>
      {hasChildren ? <Collapse in={isOpen}>{childrenLinks}</Collapse> : null}
    </>
  );
};

const SidebarMenuSection = ({ title, menuItems }: SidebarMenuSectionProps) => {
  return (
    <div>
      <Text size="xs" className="text-fjord-grey-500 mb-[14px]">
        {title}
      </Text>

      {menuItems.map((item) => {
        if (!item) return null;

        const { link, key, ...rest } = item;

        return (
          <SidebarMenuItem
            key={key ?? link ?? rest.label}
            link={link}
            {...rest}
          />
        );
      })}
    </div>
  );
};

export const SidebarMenu = ({ menuItems }: SidebarMenuProps) => {
  const { t } = useTranslation("common", {
    keyPrefix: "menuItems.sections"
  });

  const mainMenuItems = menuItems.filter(
    (item) =>
      item && ["/", "/products", "/vouchers"].includes(item.key ?? item.link!)
  );

  const transactionMenuItems = menuItems.filter(
    (item) =>
      item &&
      [
        "/orders",
        "/transaction-history",
        "/recurring-orders",
        "/disbursement",
        "/billing"
      ].includes(item.key ?? item.link!)
  );

  const preferencesMenuItems = menuItems.filter(
    (item) =>
      item &&
      ["/users", "/settings", "/logout"].includes(item.key ?? item.link!)
  );

  return (
    <nav className="grow pt-4 ">
      <ConditionalRendering condition={mainMenuItems.length > 0}>
        <SidebarMenuSection title={t("mainMenu")} menuItems={mainMenuItems} />
      </ConditionalRendering>

      {transactionMenuItems.length > 0 && (
        <>
          <hr className="my-6 border-0 h-px w-full bg-mischka-100" />
          <SidebarMenuSection
            title={t("transaction")}
            menuItems={transactionMenuItems}
          />
        </>
      )}

      {preferencesMenuItems.length > 0 && (
        <>
          <hr className="my-6 border-0 h-px w-full bg-mischka-100" />
          <SidebarMenuSection
            title={t("preferences")}
            menuItems={preferencesMenuItems}
          />
        </>
      )}
    </nav>
  );
};
